import { SmartAudienceKind, SmartAudienceListItemDto } from "@/models/club";
import { EVERGREEN_KINDS, audienceAction, audienceDiscountPath, audiencePath, audienceTitle, lastSentNote, offersDiscount } from "@/modules/smartAudience/text";
import { DashboardOverview } from "@/services/dashboard";
import { BarberDashboardOverview } from "@/services/barberDashboard/types";
import {
  IconCake,
  IconCalendar,
  IconCalendarX,
  IconConfetti,
  IconGift,
  IconReceipt2,
  IconRepeat,
  IconSparkles,
  IconSpeakerphone,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import { faNumber, rialText } from "./format";
import { SectionIcon } from "./sections";
import { Suggestion } from "./Suggestions";

// Persian verbs agree with the count: "۱ مشتری … نیامده" but "۵ مشتری … نیامده‌اند".
const plural = (count: number, verb: string) => (count === 1 ? verb : `${verb}‌اند`);

// Customers who came before but not in the last `days` days (the API's "lapsed").
const lapsedSuggestion = (count: number, days: number, yours: boolean): Suggestion => ({
  key: "lapsed",
  tone: "people",
  icon: IconUsers,
  title: `${faNumber(count)} مشتری${yours ? "‌تان" : ""} بیش از ${faNumber(days)} روز است که ${plural(count, "نیامده")}`,
  text: `با یک پیامک دوباره ${count === 1 ? "دعوتش" : "دعوتشان"} کنید.`,
  action: "دعوت دوباره با پیامک",
  to: "/dashboard/marketing",
});

export function managerSuggestions(opportunities?: DashboardOverview["opportunities"]): Suggestion[] {
  if (!opportunities) return [];
  const list: Suggestion[] = [];
  const lapsed = opportunities.lapsedCustomers;
  if (lapsed?.count > 0) list.push(lapsedSuggestion(lapsed.count, lapsed.days, false));
  const tomorrow = opportunities.tomorrowFreeCapacity;
  if (tomorrow?.hasSchedule && tomorrow.freeHours > 0)
    list.push({
      key: "tomorrow",
      tone: "sales",
      icon: IconCalendar,
      title: `فردا ${faNumber(tomorrow.freeHours, 1)} ساعت ظرفیت خالی دارید`,
      text: "با یک تخفیف محدود، ساعت‌های خالی را پر کنید.",
      action: "ارسال پیامک تخفیف",
      to: "/dashboard/discount",
    });
  return [...list, ...unpaidSuggestions(opportunities)];
}

// Bookings waiting for their deposit: a to-do, not an SMS audience, so it stays beside the smart hints.
export function unpaidSuggestions(opportunities?: DashboardOverview["opportunities"]): Suggestion[] {
  const unpaid = opportunities?.unpaidBookings;
  if (!unpaid || unpaid.customers <= 0) return [];
  return [
    {
      key: "unpaid",
      tone: "salon",
      icon: IconReceipt2,
      title: `${faNumber(unpaid.customers)} مشتری ${plural(unpaid.customers, "رزرو کرده")} ولی هنوز ${plural(unpaid.customers, "پرداخت نکرده")}`,
      text: `${rialText(unpaid.amount)} در انتظار وصول است.`,
      action: "پیگیری رزروها",
      to: "/dashboard/invoices",
    },
  ];
}

const evergreenIcons: Record<number, SectionIcon> = {
  [EVERGREEN_KINDS.openSlots]: IconCalendar,
  [EVERGREEN_KINDS.announcement]: IconSparkles,
  [EVERGREEN_KINDS.specialOffer]: IconGift,
  [EVERGREEN_KINDS.welcomeNew]: IconUserPlus,
  [EVERGREEN_KINDS.businessUpdate]: IconSpeakerphone,
  [EVERGREEN_KINDS.greeting]: IconConfetti,
};

const audienceIcon = (kind?: SmartAudienceKind) =>
  (kind !== undefined && evergreenIcons[kind]) ||
  (kind === SmartAudienceKind.Value1
    ? IconRepeat
    : kind === SmartAudienceKind.Value3 || kind === SmartAudienceKind.Value8 || kind === SmartAudienceKind.Value11
      ? IconCake
      : kind === SmartAudienceKind.Value5
        ? IconCalendarX
        : kind === SmartAudienceKind.Value7
          ? IconCalendar
          : IconUsers);

// A smart audience as a home hint; tapping it opens the SMS flow with the audience already chosen.
export function audienceSuggestion(
  audience: SmartAudienceListItemDto,
  { personal = false, merchantUserId, now = new Date() }: { personal?: boolean; merchantUserId?: number; now?: Date } = {},
): Suggestion {
  return {
    key: `audience:${audience.key}`,
    tone:
      audience.kind === SmartAudienceKind.Value1 ||
      audience.kind === SmartAudienceKind.Value7 ||
      (audience.kind as number) === EVERGREEN_KINDS.openSlots ||
      (audience.kind as number) === EVERGREEN_KINDS.specialOffer
        ? "sales"
        : "people",
    icon: audienceIcon(audience.kind),
    title: audienceTitle(audience, personal),
    text: lastSentNote(audience.lastSentAt, now) ?? "متن آماده است؛ هزینه را قبل از ارسال می‌بینید.",
    action: audienceAction(audience.kind),
    to: audiencePath(audience.key ?? "", merchantUserId),
    discountTo: offersDiscount(audience.kind) ? audienceDiscountPath(audience.key ?? "", merchantUserId) : undefined,
  };
}

const birthdayWhen = (daysUntil: number) =>
  daysUntil <= 0 ? "امروز" : daysUntil === 1 ? "فردا" : `${faNumber(daysUntil)} روز دیگر`;

export function stylistSuggestions(report?: BarberDashboardOverview): Suggestion[] {
  if (!report) return [];
  const list: Suggestion[] = [];
  const birthdays = report.followUps?.upcomingBirthdays ?? [];
  if (birthdays.length > 0) {
    const first = birthdays[0];
    list.push({
      key: "birthdays",
      tone: "people",
      icon: IconCake,
      title:
        birthdays.length === 1
          ? `تولد ${first.fullName?.trim() || "یکی از مشتری‌هایتان"}، ${birthdayWhen(first.daysUntil)}`
          : `تولد ${faNumber(birthdays.length)} مشتری‌تان نزدیک است`,
      text: "یک پیامک تبریک، رابطه‌تان با مشتری را گرم نگه می‌دارد.",
      action: "ارسال پیامک تبریک",
      to: "/dashboard/sendMessage",
    });
  }
  const lapsed = report.followUps?.lapsedCustomers;
  if (lapsed?.count > 0) list.push(lapsedSuggestion(lapsed.count, lapsed.days, true));
  const tomorrow = report.schedule?.tomorrowFreeCapacity;
  if (tomorrow?.hasSchedule && tomorrow.freeHours > 0)
    list.push({
      key: "tomorrow",
      tone: "sales",
      icon: IconCalendar,
      title: `فردا ${faNumber(tomorrow.freeHours, 1)} ساعت خالی دارید`,
      text: "برنامه‌ی فردا را ببینید و وقت‌های خالی را پر کنید.",
      action: "مشاهده‌ی تقویم",
      to: "/dashboard/calendar",
    });
  const unpaid = report.followUps?.unpaidBookingCount ?? 0;
  if (unpaid > 0)
    list.push({
      key: "unpaid",
      tone: "salon",
      icon: IconReceipt2,
      title: `${faNumber(unpaid)} رزروتان هنوز پرداخت نشده`,
      text: "فاکتورهای این رزروها را بررسی کنید.",
      action: "مشاهده‌ی فاکتورها",
      to: "/dashboard/invoices",
    });
  return list;
}
